import { useState, useEffect } from 'react';
import { useAuth } from "./AuthProvider";
import axios from 'axios';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { useFormik } from 'formik';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { addDays } from 'date-fns';
import SupportedLocales from './locales.js';
import './CreateGame.css';
import HoverHelp from './game/HoverHelp';

const MAX_ROUNDS = 10;
const MAX_TEAMS = 7;

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required'),
  rounds: Yup.number()
    .required('Number of rounds is required')
    .min(1, 'Must be at least 1 round')
    .max(MAX_ROUNDS, 'Maximum of 10 rounds'),
  teams: Yup.number()
    .required('Number of rounds is required')
    .min(2, 'Must be at least 2')
    .max(MAX_TEAMS, 'Maximum of 7 teams'),
  marketDemand: Yup.array()
      .of(Yup.number().min(1000).max(1_000_000_000)),
});

const formatISODate = (date) => {
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
}

function toUTCISOString(dateString) {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toISOString();
}

const populateMarketOverviews = (marketOverviews, templates, rounds) => {
    const r = Math.min(rounds, MAX_ROUNDS);
    const updated = [];
    for (let i = 1; i <= r; i++) {
      const roundTemplates = templates.filter(t => t.round === i);
      updated.push(...roundTemplates);
    }
    return updated;
};

const MarketOverview = ({language, round, marketOverview, formik}) => {
    const { t } = useTranslation();
    const marketOverviewLabel = `${t('games.market-overview')} ${language}`;
    const handleMarketOverviewChange = (id, event) => {
      const updatedMarketOverview = formik.values.marketOverview.map((mo, i) => {
          const newMarketOverview = {
              ...mo,
              description: event.target.value
          };
          return mo.id === id ? newMarketOverview : mo;
      });
      formik.setFieldValue('marketOverview', updatedMarketOverview);
    };
    return <tr>
        <td colSpan={2}>
           <Form.Group key={marketOverview.id} className="mb-3 d-flex align-items-center">
             <Form.Control
               type="text"
               as="textarea"
               rows={4}
               placeholder={marketOverviewLabel}
               value={marketOverview ? marketOverview.description : ''}
               onChange={(event) => handleMarketOverviewChange(marketOverview.id, event)}
               onBlur={() => formik.setFieldTouched(`marketOverview[${round}]`)}
               isInvalid={formik.touched.marketOverview && Array.isArray(formik.errors.marketOverview) && !!formik.errors.marketOverview?.[round]}
             />
             <Form.Control.Feedback type="invalid">
               {formik.errors.marketOverview?.[round]}
             </Form.Control.Feedback>
           </Form.Group>
        </td>
    </tr>
};

const RoundConfiguration = ({round, marketOverviews, marketDemand, deadlines, formik}) => {
    const { t } = useTranslation();
    const demand = (marketDemand?.length >= round ? marketDemand[round - 1] : 0) ?? '';
    const deadline = (deadlines?.length >= round ? deadlines[round - 1] : new Date()) ?? new Date();
    const handleMarketDemandChange = (index, event) => {
         const updatedMarketDemand = formik.values.marketDemand.map((d, i) =>
           i === (index - 1) ? event.target.value : d
         );
         formik.setFieldValue('marketDemand', updatedMarketDemand);
    };
    const handleDeadlineChange = (index, event) => {
      const updatedDeadline = formik.values.deadline.map((dl, i) =>
        i === (index - 1) ? event.target.value : dl
      );
      formik.setFieldValue('deadline', updatedDeadline);
    };
    const marketDemandLabel = t('games.market-demand') + ' (' + t('unit.boxes') + ')';
    const deadlineLabel = t('games.round-deadline');

    return (
        <Form.Group className="mb-3">
            <Table key={round} className="single-border-table">
                <thead>
                    <tr>
                        <td colSpan={2} className="text-center"><b>{t('games.round')} {round}</b></td>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <Form.Label>{marketDemandLabel}</Form.Label>
                        </td>
                        <td>
                            <Form.Group key={round} className="mb-3 d-flex align-items-center">
                               <Form.Control
                                 type="text"
                                 placeholder={marketDemandLabel}
                                 value={demand}
                                 onChange={(event) => handleMarketDemandChange(round, event)}
                                 onBlur={() => formik.setFieldTouched(`marketDemand[${round}]`)}
                                 isInvalid={formik.touched.marketDemand && Array.isArray(formik.errors.marketDemand) && !!formik.errors.marketDemand?.[round]}
                               />
                               <Form.Control.Feedback type="invalid">
                                 {formik.errors.marketDemand?.[round]}
                               </Form.Control.Feedback>
                            </Form.Group>
                        </td>
                    </tr>
                    {
                        SupportedLocales.map(locale => {
                            const marketOverview = marketOverviews?.find(mo => mo.round === round && mo.locale === locale);
                            return <MarketOverview
                                key={round + locale}
                                round={round}
                                language={locale}
                                marketOverview={marketOverview}
                                formik={formik}
                        />})
                    }
                    <tr>
                        <td><Form.Label>{deadlineLabel}</Form.Label></td>
                        <td>
                            <Form.Group key={round} className="mb-3 d-flex align-items-center">
                             <Form.Control
                               type="date"
                               placeholder={deadlineLabel}
                               value={deadline ? formatISODate(deadline) : ''}
                               onChange={(event) => handleDeadlineChange(round, event)}
                               onBlur={() => formik.setFieldTouched(`deadline[${round}]`)}
                               isInvalid={formik.touched.deadline && Array.isArray(formik.errors.deadline) && !!formik.errors.deadline?.[round]}
                             />
                             <Form.Control.Feedback type="invalid">
                               {formik.errors.deadline?.[round]}
                             </Form.Control.Feedback>
                           </Form.Group>
                        </td>
                    </tr>
                </tbody>
            </Table>
        </Form.Group>
    );
};

const CreateGame = ({ show, onClose, setCreating }) => {
    const { t, i18n } = useTranslation();
    const auth = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [marketOverviewTemplates, setMarketOverviewTemplates] = useState([]);
    const [error, setError] = useState(null);
    // TODO: default rounds to settings
    const defaultRounds = 4;
    const today = new Date();
    const formik = useFormik({
      initialValues: {
        name: '',
        rounds: defaultRounds,
        teams: 4,
        playersPerTeam: 4,
        marketDemand: Array.from({ length: defaultRounds }).map((_, i) => 1_000_000),
        deadline: Array.from({ length: defaultRounds }).map((_, i) => formatISODate(addDays(today, (i + 1) * 7))),
        marketOverview: Array.from({ length: defaultRounds * SupportedLocales.length }).map((_, i) => ''),
      },
      validationSchema: validationSchema,
      onSubmit: values => {
         const vals = {...values};
         vals.deadline = vals.deadline.map(toUTCISOString);
         const mergedMarketOverviews = Array.from({ length: formik.values.rounds }).map((_, i) => {
             const roundMarketOverviews = vals.marketOverview.filter(mo => mo.round === i + 1);
             // TODO: this should be dynamic and based on SupportedLocales
             return {
                 descriptions: {
                     "et": roundMarketOverviews.find(rmo => rmo.locale === "et").description,
                     "en": roundMarketOverviews.find(rmo => rmo.locale === "en").description
                 }
             };
         });
         vals.marketOverview = mergedMarketOverviews;
         console.log("vals: ", vals);
         axios.post('api/games', vals, {
               headers: {
                 Authorization: `Bearer ${auth.token}`,
                 'Content-Type': 'application/json'
               },
             })
           .then(response => {
             const data = response.data;
             navigate(`/games/${data.id}`);
             setCreating(false);
           })
           .catch(error => {
               console.log(error);
           });
      },
    });

    const handleRoundsChange = (e) => {
        const rounds = e.target.value;
        formik.setFieldValue('rounds', rounds);

        const mergedMarketOverviews = populateMarketOverviews(formik.values.marketOverview, marketOverviewTemplates, rounds);
        formik.setFieldValue('marketOverview', mergedMarketOverviews);

        const marketDemandDiff = rounds - formik.values.marketDemand.length;
        if (marketDemandDiff > 0) {
            for (let i = 0; i < marketDemandDiff; i++) {
                formik.values.marketDemand.push(1_000_000);
            }
        } else {
            for (let i = marketDemandDiff; i < 0; i++) {
                formik.values.marketDemand.pop();
            }
        }

        const deadlineDiff = rounds - formik.values.deadline.length;
        if (deadlineDiff > 0) {
            for (let i = 0; i < deadlineDiff; i++) {
                const deadline = formatISODate(addDays(today, formik.values.deadline.length * 7));
                formik.values.deadline.push(deadline);
            }
        } else {
            for (let i = deadlineDiff; i < 0; i++) {
                formik.values.deadline.pop();
            }
        }
    };

    const handleTeamsChange = (e) => {
        const teams = e.target.value;
        formik.setFieldValue('teams', teams);
    };

    const handlePlayersPerTeamChange = (e) => {
        const playersPerTeam = e.target.value;
        formik.setFieldValue('playersPerTeam', playersPerTeam);
    };

    useEffect(() => {
      setLoading(true);
      axios.get(`/api/admin/market-overview-templates`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      })
        .then(response => {
          setMarketOverviewTemplates(response.data);
          const mergedMarketOverviews = populateMarketOverviews(formik.values.marketOverview, response.data, formik.values.rounds);
          formik.setFieldValue('marketOverview', mergedMarketOverviews);
          setLoading(false);
        })
        .catch(error => {
          console.log(error);
          setError(error);
          setLoading(false);
        });
    }, [auth.token, i18n.language]);

       if (loading) return <p>{t('common.loading')}</p>;
       if (error) return <p>{t('market-overview-templates.loading-error')}: {error.message}</p>;

       return (
             <Modal show={show} onHide={onClose} size="lg">
               <Form onSubmit={formik.handleSubmit}>
               <Modal.Header closeButton>
                 <Modal.Title>{t('games.create')}</Modal.Title>
               </Modal.Header>
               <Modal.Body>
                 <Form.Group className="mb-3">
                   <Form.Label>{t('games.name')}</Form.Label>
                   <Form.Control
                     id="name"
                     type="text"
                     onChange={formik.handleChange}
                     onBlur={formik.handleBlur}
                     value={formik.values.name}
                     isInvalid={formik.touched.name && !!formik.errors.name}
                   />
                   <Form.Control.Feedback type="invalid">
                     {formik.errors.name}
                   </Form.Control.Feedback>
                 </Form.Group>
                 <Form.Group className="mb-3">
                   <Form.Label>{t('games.number-of-rounds')}</Form.Label>
                   <Form.Control
                     id="rounds"
                     type="text"
                     onChange={handleRoundsChange}
                     onBlur={formik.handleBlur}
                     value={formik.values.rounds}
                     isInvalid={formik.touched.rounds && !!formik.errors.rounds}
                   />
                     <Form.Control.Feedback type="invalid">
                       {formik.errors.rounds}
                     </Form.Control.Feedback>
                 </Form.Group>
                 <Form.Group className="mb-3">
                   <Form.Label>{t('games.number-of-teams')}</Form.Label>
                   <Form.Control
                     id="teams"
                     type="text"
                     onChange={handleTeamsChange}
                     onBlur={formik.handleBlur}
                     value={formik.values.teams}
                     isInvalid={formik.touched.teams && !!formik.errors.teams}
                   />
                     <Form.Control.Feedback type="invalid">
                       {formik.errors.teams}
                     </Form.Control.Feedback>
                 </Form.Group>
                 <Form.Group className="mb-3">
                   <Form.Label>{t('games.max-players-per-team')}</Form.Label>
                   <Form.Control
                     id="playersPerTeam"
                     type="text"
                     onChange={handlePlayersPerTeamChange}
                     onBlur={formik.handleBlur}
                     value={formik.values.playersPerTeam}
                     isInvalid={formik.touched.playersPerTeam && !!formik.errors.playersPerTeam}
                   />
                     <Form.Control.Feedback type="invalid">
                       {formik.errors.playersPerTeam}
                     </Form.Control.Feedback>
                 </Form.Group>
                 {
                     Array.from({ length: Math.min(formik.values.rounds, MAX_ROUNDS) }).map((_, i) =>
                         <RoundConfiguration key={i}
                             round={i + 1}
                             marketOverviews={formik.values.marketOverview}
                             marketDemand={formik.values.marketDemand}
                             deadlines={formik.values.deadline}
                             formik={formik} />)
                 }

                <Form.Group className="mb-3">
                  {typeof formik.errors.marketDemand === 'string' && (
                    <div className="invalid-feedback d-block mb-3">
                      {formik.errors.marketDemand}
                    </div>
                  )}
                </Form.Group>
                 <Form.Group className="mb-3">
                   {typeof formik.errors.deadline === 'string' && (
                     <div className="invalid-feedback d-block mb-3">
                       {formik.errors.deadline}
                     </div>
                   )}
                 </Form.Group>
                 <Form.Group className="mb-3">
                   {typeof formik.errors.marketOverview === 'string' && (
                     <div className="invalid-feedback d-block mb-3">
                       {formik.errors.marketOverview}
                     </div>
                   )}
                 </Form.Group>
               </Modal.Body>
               <Modal.Footer>
                 <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
                 <Button type="submit" variant="primary" disabled={!(formik.isValid && formik.dirty)}>{t('common.save')}</Button>
               </Modal.Footer>
               </Form>
             </Modal>
           );
};

export default CreateGame;